/**
 * IconButton Component
 * 
 * A styled icon button component for actions.
 */

import React from 'react';
import { IconButton as MuiIconButton, Tooltip, alpha } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { colors, radii, transitions } from '../../tokens';

export type IconButtonVariant = 'default' | 'primary' | 'ghost';
export type IconButtonSize = 'small' | 'medium' | 'large';

export interface IconButtonProps {
  /** Icon to display */
  icon: React.ReactNode;
  /** Click handler */
  onClick?: () => void;
  /** Button variant */
  variant?: IconButtonVariant;
  /** Button size */
  size?: IconButtonSize;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Tooltip text */
  tooltip?: string;
  /** Aria label */
  'aria-label'?: string;
  /** Additional styles */
  sx?: SxProps<Theme>;
}

const getVariantStyles = (variant: IconButtonVariant) => {
  switch (variant) {
    case 'primary':
      return {
        backgroundColor: alpha(colors.primary[500], 0.1),
        color: colors.primary[500],
        '&:hover': {
          backgroundColor: alpha(colors.primary[500], 0.2),
        },
      };
    case 'ghost':
      return {
        backgroundColor: 'transparent',
        color: colors.neutral[600],
        '&:hover': {
          backgroundColor: colors.neutral[100],
          color: colors.neutral[900],
        },
      };
    default:
      return {
        backgroundColor: colors.neutral[100],
        color: colors.neutral[600],
        '&:hover': {
          backgroundColor: colors.neutral[200],
          color: colors.neutral[900],
        },
      };
  }
};

const getSizeStyles = (size: IconButtonSize) => {
  switch (size) {
    case 'small':
      return {
        width: 32,
        height: 32,
        '& svg': { fontSize: 18 },
      };
    case 'medium':
      return {
        width: 40,
        height: 40,
        '& svg': { fontSize: 20 },
      };
    case 'large':
      return {
        width: 48,
        height: 48,
        '& svg': { fontSize: 24 },
      };
    default:
      return {};
  }
};

export const IconButton: React.FC<IconButtonProps> = ({
  icon,
  onClick,
  variant = 'default',
  size = 'medium',
  disabled = false,
  tooltip,
  'aria-label': ariaLabel,
  sx,
}) => {
  const variantStyles = getVariantStyles(variant);
  const sizeStyles = getSizeStyles(size);

  const button = (
    <MuiIconButton
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      sx={{
        ...variantStyles,
        ...sizeStyles,
        borderRadius: `${radii.md}px`,
        transition: transitions.fast,
        '&:disabled': {
          opacity: 0.5,
        },
        ...sx,
      }}
    >
      {icon}
    </MuiIconButton>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow>
        {button}
      </Tooltip>
    );
  }

  return button;
};

export default IconButton;

